import { useEffect, useRef } from 'react';

import { useDashboard } from '@/features/dashboard/api';
import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import type { DashboardTab, MarketApps } from '@/features/dashboard/types';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
  selectInitialDashboardName,
  selectIsInitialized
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { resetDraft, setAllDashboardsData } from '@/features/dashboard/state/dashboardEdit.slice';
import {
  initializeDashboardEditThunk,
  reinitializeDraftThunk
} from '@/features/dashboard/state/dashboardEdit.thunks';

type UseDashboardEditInitParams = {
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  activeTabIndex?: number;
};

/**
 * Initializes dashboard edit draft from React Query cache.
 * Runs once on mount, resets on unmount.
 * Handles dashboard switching by reinitializing if the dashboard changes.
 */
export const useDashboardEditInit = ({
  dashboardName,
  initialDashboardName,
  dashboardConfig,
  activeTabIndex = 0
}: UseDashboardEditInitParams) => {
  const dispatch = useAppDispatch();
  const isInitialized = useAppSelector(selectIsInitialized);
  const currentEditingDashboard = useAppSelector(selectInitialDashboardName);

  // Track if we're currently reinitializing to prevent double dispatch
  const isReinitializingRef = useRef(false);

  // Fetch dashboard structure from React Query
  const { data: dashboards, isLoading } = useDashboard();

  // Initialize draft on mount or reinitialize on dashboard switch
  useEffect(() => {
    if (!dashboards) return;

    // For new dashboards not yet saved, create an empty tab structure
    const isNewDashboard = !(initialDashboardName in dashboards);
    const serverData: DashboardTab = isNewDashboard
      ? {
          [TAB_DEFAULT_BASE_NAME]: {
            id: generateUniqueUUID(new Set()),
            content: []
          }
        }
      : dashboards[initialDashboardName];

    // Case 1: First initialization
    if (!isInitialized) {
      const allData = isNewDashboard
        ? { ...dashboards, [initialDashboardName]: serverData }
        : dashboards;
      dispatch(setAllDashboardsData({ data: allData }));

      // Use thunk for initialization to keep hook clean
      dispatch(
        initializeDashboardEditThunk({
          dashboardName,
          initialDashboardName,
          dashboardConfig,
          currentDashboardTabs: serverData,
          tabNames: Object.keys(serverData),
          activeTab: activeTabIndex
        })
      );
      return;
    }

    // Case 2: Dashboard changed while editing - reinitialize
    // This handles when user navigates to a different dashboard while in edit mode
    if (
      currentEditingDashboard &&
      currentEditingDashboard !== initialDashboardName &&
      !isReinitializingRef.current
    ) {
      isReinitializingRef.current = true;

      dispatch(
        reinitializeDraftThunk({
          dashboardName: initialDashboardName,
          activeTab: activeTabIndex,
          allDashboardsData: dashboards
        })
      );
      isReinitializingRef.current = false;
    }
  }, [
    dashboards,
    dashboardName,
    initialDashboardName,
    dashboardConfig,
    activeTabIndex,
    dispatch,
    isInitialized,
    currentEditingDashboard
  ]);

  // Reset draft on unmount
  useEffect(() => {
    return () => {
      dispatch(resetDraft());
    };
  }, [dispatch]);

  // Draft is considered stale if initialized but for a different dashboard
  const isDraftStale =
    isInitialized && currentEditingDashboard && currentEditingDashboard !== initialDashboardName;

  return { isInitialized: isInitialized && !isDraftStale, isLoading };
};
